import { HttpErrorResponse } from "@angular/common/http";
import { IPresignedURLResponse } from "../models";
import { CommonService, ContentsService, ToasterService } from "../services";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { Constants } from "../common";

export class AppsFunctions {
    private static readonly responsiveStyle = `
<style id="zignflix-responsive-runtime">
  :root {
    color-scheme: light dark;
    text-size-adjust: 100%;
    -webkit-text-size-adjust: 100%;
  }
  html,
  body {
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 100%;
    margin: 0;
  }
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  body {
    overflow: hidden;
  }
  img,
  svg,
  video,
  canvas,
  iframe {
    display: block;
    max-width: 100%;
  }
  img,
  video {
    height: auto;
  }
  iframe {
    width: 100%;
    max-height: 100%;
  }
  body > * {
    max-width: 100%;
    max-height: 100%;
  }
  @media (max-width: 600px), (max-height: 500px) {
    body {
      font-size: clamp(12px, 2.5vmin, 16px);
    }
    body > *,
    [class*="container"],
    [class*="card"],
    [class*="widget"],
    [style*="min-width"] {
      min-width: 0 !important;
      max-width: 100%;
    }
  }
</style>`;

    static PrepareResponsiveHtml(widgetHtml: string): string {
        let html = widgetHtml.trim().replace(/^\s*<!doctype[^>]*>\s*/i, '');

        if (!/<meta[^>]+name=["']viewport["']/i.test(html)) {
            html = html.replace(
                /<head([^>]*)>/i,
                '<head$1><meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">'
            );
        }

        if (!/id=["']zignflix-responsive-runtime["']/i.test(html)) {
            html = /<\/head>/i.test(html)
                ? html.replace(/<\/head>/i, `${AppsFunctions.responsiveStyle}</head>`)
                : `${AppsFunctions.responsiveStyle}${html}`;
        }

        return `<!DOCTYPE html>\n${html}`;
    }

    static UploadHtml(
        widgetHtml: string,
        name: string,
        appName: string,
        appId: number,
        isAppEditingMode: boolean,
        contentsService: ContentsService,
        commonService: CommonService,
        toaster: ToasterService,
        router: Router,
        subscriptions: Subscription[],
        app_duration?: number,
        refreshTiming?: number
    ): void {
        const htmlContent = AppsFunctions.PrepareResponsiveHtml(widgetHtml);
        const timestamp = new Date().getTime();
        const fileName = `${name}_${timestamp}.html`;
        const fileType = 'text/html';
        const file = new File([htmlContent], fileName, { type: fileType });
        const noEmptySpace = name.trim();

        if (name && noEmptySpace) {
            commonService.isLoading = true;

            contentsService.getPresignedURLForS3(fileName, fileType, file.size)
                .subscribe({
                    next: (presignedData: IPresignedURLResponse) => {
                        if (!presignedData?.url) {
                            throw new Error('No presigned URL received');
                        }

                        contentsService.uploadFileToS3(presignedData.url, file)
                            .subscribe({
                                next: () => {
                                    const formData: FormData = new FormData();
                                    formData.append('title', name);
                                    formData.append('file_upload', presignedData.url.split('?')[0]);
                                    formData.append('file_format', Constants.fileFormats.app);
                                    formData.append('file_size', file.size.toString());
                                    formData.append('file_duration', app_duration ? app_duration.toString() : '');
                                    formData.append('resolution', '');
                                    formData.append('thumbnail', '0');
                                    formData.append('app_type', appName);
                                    formData.append('category', 'app');
                                    formData.append('source', Constants.applicationName);
                                    formData.append('refresh_timing', refreshTiming ? refreshTiming.toString() : '');

                                    if (!isAppEditingMode) {
                                        subscriptions.push(
                                            contentsService.addContents(formData).subscribe({
                                                next: () => {
                                                    commonService.isLoading = false;
                                                    toaster.success(Constants.notifyMessages.addSuccessContent);
                                                    router.navigate([Constants.routingPaths.apps]);
                                                },
                                                error: (error: HttpErrorResponse) => {
                                                    commonService.isLoading = false;
                                                    toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.addErrorContent);
                                                }
                                            })
                                        );
                                    } else {
                                        subscriptions.push(
                                            contentsService.updateContents(appId, formData).subscribe({
                                                next: () => {
                                                    commonService.isLoading = false;
                                                    toaster.success(Constants.notifyMessages.editSuccessApp);
                                                    // router.navigate([Constants.routingPaths.apps]);
                                                },
                                                error: (error: HttpErrorResponse) => {
                                                    commonService.isLoading = false;
                                                    toaster.error(error?.error?.message ? error.error.message : Constants.notifyMessages.editErrorApp);
                                                }
                                            })
                                        );
                                    }
                                },
                                error: (uploadError: HttpErrorResponse) => {
                                    commonService.isLoading = false;
                                    toaster.error('File upload failed: ' + (uploadError.message || 'Unknown error'));
                                }
                            });
                    },
                    error: (urlError: HttpErrorResponse) => {
                        commonService.isLoading = false;
                        toaster.error(urlError?.error?.message ? urlError.error.message : Constants.notifyMessages.commonErrorLoading);
                    }
                });
        } else {
            if (!noEmptySpace) {
                toaster.warning(`Can't save without entering app name!`);
            }
        }
    }
}
